// Contact section (US-110) — email, phone, calendar URL, contact priority,
// plus the no-channel warning and the always-on info callout. JSX lifted
// verbatim from BusinessEditor.

import { useT } from '@barel/i18n';
import { Banner, Card, FieldAssembly, Input, Radio, RadioGroup } from '@barel/ui';

import type { BusinessDraft } from '../lib/defaults';
import { SectionHeader } from './SectionHeader';
import type { SetField } from './types';

export interface ContactSectionProps {
  email: BusinessDraft['email'];
  phone: BusinessDraft['phone'];
  calendarUrl: BusinessDraft['calendarUrl'];
  contactPriority: BusinessDraft['contactPriority'];
  onChange: SetField;
}

export function ContactSection({
  email,
  phone,
  calendarUrl,
  contactPriority,
  onChange,
}: ContactSectionProps) {
  const t = useT('businesses');

  return (
    <Card padding="lg" radius="2xl" data-testid="business-section-contact">
      <SectionHeader title={t('sections.contact')} subtitle={t('contact.subtitle')} />
      <div className="flex flex-col gap-4">
        <FieldAssembly label={t('contact.emailLabel')}>
          <Input
            type="email"
            value={email}
            onChange={(event) => onChange('email', event.target.value)}
            data-testid="business-email"
          />
        </FieldAssembly>
        <FieldAssembly label={t('contact.phoneLabel')}>
          <Input
            type="tel"
            value={phone}
            onChange={(event) => onChange('phone', event.target.value)}
            data-testid="business-phone"
          />
        </FieldAssembly>
        <FieldAssembly label={t('contact.calendarLabel')}>
          <Input
            type="url"
            value={calendarUrl}
            onChange={(event) => onChange('calendarUrl', event.target.value)}
            data-testid="business-calendar"
          />
        </FieldAssembly>
        <FieldAssembly label={t('contact.priorityLabel')}>
          <RadioGroup
            value={contactPriority}
            onValueChange={(value) =>
              onChange('contactPriority', value as BusinessDraft['contactPriority'])
            }
            aria-label={t('contact.priorityLabel')}
          >
            {(['calendar_first', 'phone_first', 'calendar_only', 'phone_only'] as const).map(
              (key) => (
                <label key={key} className="flex items-center gap-2 text-sm text-secondary">
                  <Radio value={key} data-testid={`business-priority-${key}`} />
                  {t(`contact.priority.${key}`)}
                </label>
              ),
            )}
          </RadioGroup>
        </FieldAssembly>
        {!calendarUrl.trim() && !phone.trim() && !email.trim() ? (
          <Banner tone="warning" data-testid="business-contact-warning">
            {t('contact.warningNoChannel')}
          </Banner>
        ) : null}
        <Banner tone="info" data-testid="business-contact-callout">
          <div className="flex flex-col gap-1">
            <strong>{t('contact.calloutTitle')}</strong>
            <span>{t('contact.calloutBody')}</span>
            <span className="text-xs italic text-tertiary">{t('contact.calloutFootnote')}</span>
          </div>
        </Banner>
      </div>
    </Card>
  );
}
